import React, { useEffect, useRef, useState } from 'react'
import { MdDateRange, MdCheck } from 'react-icons/md'
import { BiChevronDown } from 'react-icons/bi'
import { TICKET_PERIODS, formatTicketPeriodRange, normalizeTicketPeriod, toDateInputValue } from '../../utils/ticketDateRange'

// فلتر الفترة الزمنية لقائمة التذاكر: فترات جاهزة + فترة مخصصة (من / إلى) في نفس القائمة المنسدلة.
const TicketDateFilter = ({ period, from, to, onChange, lang = 'ar' }) => {
    const isAr = lang === 'ar';
    const activePeriod = normalizeTicketPeriod(period);
    const [open, setOpen] = useState(false);
    const [customFrom, setCustomFrom] = useState(from || '');
    const [customTo, setCustomTo] = useState(to || '');
    const [showCustom, setShowCustom] = useState(activePeriod === 'custom');
    const wrapperRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const close = (e) => { if (!wrapperRef.current?.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', close);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', close);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        setCustomFrom(from || '');
        setCustomTo(to || '');
        setShowCustom(activePeriod === 'custom');
    }, [activePeriod, from, open, to]);

    const activeMeta = TICKET_PERIODS.find((item) => item.value === activePeriod) || TICKET_PERIODS[0];
    const activeRange = formatTicketPeriodRange(activePeriod, from, to, lang);
    const customInvalid = Boolean(customFrom && customTo && customFrom > customTo);
    const canApplyCustom = Boolean((customFrom || customTo) && !customInvalid);

    const pick = (value) => {
        if (value === 'custom') {
            setShowCustom(true);
            if (!customFrom && !customTo) {
                const now = new Date();
                setCustomFrom(toDateInputValue(new Date(now.getFullYear(), now.getMonth(), 1)));
                setCustomTo(toDateInputValue(now));
            }
            return;
        }
        onChange?.({ period: value, from: '', to: '' });
        setOpen(false);
    };

    const applyCustom = () => {
        if (!canApplyCustom) return;
        onChange?.({ period: 'custom', from: customFrom, to: customTo });
        setOpen(false);
    };

    const dateInputClass = 'w-full h-8 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100';

    return (
        <div ref={wrapperRef} className={`relative ${open ? 'z-[9999]' : 'z-10'}`}>
            <label className="text-xs text-slate-500 font-semibold mb-1.5 block">{isAr ? 'الفترة الزمنية' : 'Date range'}</label>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className={`flex w-full min-h-[38px] items-center justify-between gap-2 rounded-lg border bg-white px-3 text-sm text-slate-700 transition-all hover:border-indigo-400 hover:bg-slate-50 ${open ? 'border-indigo-400 ring-2 ring-indigo-100' : activePeriod !== 'all' ? 'border-indigo-200' : 'border-slate-200'}`}
            >
                <span className="flex items-center gap-2 min-w-0">
                    <MdDateRange size={17} className="text-indigo-500 shrink-0" />
                    <span className="flex flex-col items-start min-w-0 leading-tight">
                        <span className="font-semibold truncate">{isAr ? activeMeta.ar : activeMeta.en}</span>
                        {activeRange && <span className="text-[10px] text-slate-400 truncate">{activeRange}</span>}
                    </span>
                </span>
                <BiChevronDown size={18} className={`text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <div className="absolute top-full mt-1 w-[290px] max-w-[calc(100vw-32px)] rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl" style={{ insetInlineStart: 0 }}>
                    {TICKET_PERIODS.map((item) => {
                        const isActive = item.value === 'custom' ? showCustom : (!showCustom && item.value === activePeriod);
                        const range = item.value === 'custom' ? '' : formatTicketPeriodRange(item.value, '', '', lang);
                        return (
                            <button
                                key={item.value}
                                type="button"
                                onClick={() => pick(item.value)}
                                className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-start transition-colors ${isActive ? 'bg-indigo-50 text-indigo-700' : 'text-slate-700 hover:bg-slate-50'}`}
                            >
                                <span className="flex flex-col leading-tight">
                                    <span className="text-[13px] font-semibold">{isAr ? item.ar : item.en}</span>
                                    {range && <span className="text-[10px] text-slate-400">{range}</span>}
                                </span>
                                {isActive && <MdCheck size={16} className="shrink-0" />}
                            </button>
                        );
                    })}
                    {showCustom && (
                        <div className="mt-1 rounded-lg border border-slate-100 bg-slate-50 p-2.5">
                            <div className="grid grid-cols-2 gap-2">
                                <label className="text-[11px] font-semibold text-slate-500">
                                    {isAr ? 'من' : 'From'}
                                    <input type="date" value={customFrom} max={customTo || undefined} onChange={(e) => setCustomFrom(e.target.value)} className={`${dateInputClass} mt-1`} />
                                </label>
                                <label className="text-[11px] font-semibold text-slate-500">
                                    {isAr ? 'إلى' : 'To'}
                                    <input type="date" value={customTo} min={customFrom || undefined} onChange={(e) => setCustomTo(e.target.value)} className={`${dateInputClass} mt-1`} />
                                </label>
                            </div>
                            {customInvalid && (
                                <div className="mt-1.5 text-[11px] text-red-500">{isAr ? 'تاريخ البداية بعد تاريخ النهاية' : 'Start date is after end date'}</div>
                            )}
                            <button
                                type="button"
                                onClick={applyCustom}
                                disabled={!canApplyCustom}
                                className="mt-2 w-full h-8 rounded-md bg-indigo-600 text-xs font-bold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                            >
                                {isAr ? 'تطبيق الفترة' : 'Apply range'}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TicketDateFilter
